import { z } from "zod";
import { BATTLE_RULES } from "./battles";
import { EffectSchema, type Effect } from "./effects";
import { ART_KEYS, RANKS, type ArtKey } from "./ranks";
import { TRIALS } from "./trials";

/*
  NINJAS. One row per ninja the child can recruit and fight. Today they are
  the nine rank ninjas, identified by their art key so later non-rank ninjas
  (from bosses and special rounds) are more rows, not a re-keying.

  health:  hit points; 10 × the rank's trial pass mark, so beating the ninja
           takes about as much maths as passing its trial
  attack:  health the child loses per wrong answer against this ninja
  move:    the special move, usable once a battle. Its name comes from
           ranks.ts; its effect is one of the kinds in effects.ts
  slots:   how many active and passive items it can carry (brief 4.2). Data,
           shaped so a layout is a reason to pick a ninja, not only its stats
*/

export type NinjaId = ArtKey;

/** Every roster starts with this ninja, so there's always someone to fight with. */
export const STARTER_NINJA: NinjaId = "seedling";

/** Kept for callers from Phase 3; every effect kind now lives in effects.ts. */
export type MoveEffect = Effect;

const MoveSchema = z.object({
  name: z.string().min(1),
  /** Kid-facing, e.g. "Double hits for 10 seconds". */
  text: z.string().min(1),
  effect: EffectSchema,
});
export type Move = z.infer<typeof MoveSchema>;

/** Special moves by name. Every rank's move in ranks.ts must appear here. */
const MOVES: readonly Move[] = z
  .array(MoveSchema)
  .refine(
    (moves) => RANKS.every((r) => moves.some((m) => m.name === r.move)),
    "every rank's move needs an effect",
  )
  .parse([
    { name: "Leaf Leap", text: "10 more seconds", effect: { kind: "time", seconds: 10 } },
    {
      name: "Bamboo Spin",
      text: "The next 2 wrong answers don't hurt",
      effect: { kind: "shield", hits: 2 },
    },
    {
      name: "Pounce Strike",
      text: "The next 2 right answers hit double",
      effect: { kind: "charge", hits: 2, times: 2 },
    },
    { name: "Smoke Vanish", text: "Skip this question, no penalty", effect: { kind: "skip" } },
    {
      name: "Star Storm",
      text: "The next 4 right answers hit double",
      effect: { kind: "charge", hits: 4, times: 2 },
    },
    {
      name: "Thunder Dash",
      text: "Double hits for 10 seconds",
      effect: { kind: "boost", times: 2, seconds: 10 },
    },
    {
      name: "Blazing Slash",
      text: "The next 2 right answers hit triple",
      effect: { kind: "charge", hits: 2, times: 3 },
    },
    {
      name: "Dragon's Roar",
      text: "The next 3 right answers hit triple",
      effect: { kind: "charge", hits: 3, times: 3 },
    },
    {
      name: "Infinity Strike",
      text: "Triple hits for 10 seconds",
      effect: { kind: "boost", times: 3, seconds: 10 },
    },
  ]);

export const SlotsSchema = z.object({
  active: z.number().int().min(0).max(3),
  passive: z.number().int().min(0).max(2),
});
export type Slots = z.infer<typeof SlotsSchema>;

/**
 * Slot layouts by ninja (PLAN_PHASE4 2.3). The brief fixes Seedling, Fire
 * Blade and Legend; the rest give mid-rank ninjas a reason to be picked.
 */
const SLOTS: Record<ArtKey, Slots> = {
  seedling: { active: 1, passive: 0 },
  bamboo: { active: 1, passive: 1 },
  cub: { active: 2, passive: 0 },
  silent: { active: 1, passive: 2 },
  star: { active: 2, passive: 1 },
  storm: { active: 3, passive: 0 },
  fire: { active: 2, passive: 1 },
  dragon: { active: 2, passive: 2 },
  legend: { active: 3, passive: 2 },
};

export const NinjaSchema = z.object({
  id: z.enum(ART_KEYS),
  /** Which rank's ninja this is; its art, name, stats and move come from ranks.ts. */
  rankIndex: z.number().int().nonnegative(),
  name: z.string().min(1),
  /** Speed, Stealth, Power, Brain, out of 10. */
  powers: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  health: z.number().int().positive(),
  attack: z.number().int().positive(),
  move: MoveSchema,
  slots: SlotsSchema,
});
export type Ninja = z.infer<typeof NinjaSchema>;

export const NINJAS: readonly Ninja[] = z
  .array(NinjaSchema)
  .refine((ns) => new Set(ns.map((n) => n.id)).size === ns.length, "ninja ids must be unique")
  .refine((ns) => ns.some((n) => n.id === STARTER_NINJA), "the starter ninja must exist")
  .parse(
    RANKS.map((r, i) => ({
      id: r.art,
      rankIndex: i,
      name: r.name,
      powers: r.powers,
      health:
        i === 0
          ? BATTLE_RULES.starterHealth
          : TRIALS[i - 1]!.passMark * BATTLE_RULES.hitPointsPerPoint,
      attack: 1,
      move: MOVES.find((m) => m.name === r.move)!,
      slots: SLOTS[r.art],
    })),
  );

const BY_ID = new Map<string, Ninja>(NINJAS.map((n) => [n.id, n]));

export function ninjaById(id: string): Ninja | undefined {
  return BY_ID.get(id);
}

export function isNinjaId(id: unknown): id is NinjaId {
  return typeof id === "string" && BY_ID.has(id);
}

/** The ninja of a rank. Every rank has one. */
export function ninjaOfRank(rankIndex: number): Ninja {
  return NINJAS.find((n) => n.rankIndex === rankIndex) ?? NINJAS[0]!;
}
